import { useEffect, useState } from "react";
import Logo from "./Logo";
import {
  Home, Search, Tag, BarChart3, Camera, Globe, Star, Target,
  FileText, Settings, HelpCircle, Crosshair, TrendingUp, Share2, Building2, ShieldCheck, Users, ChevronDown, Lock, Layers, Wrench, Award,
} from "lucide-react";
import { DEPTS } from "./Reparti";
import { api } from "@/lib/leuteliaApi";
import { moduleAllowed } from "./PacchettoBloccato";

export function useInactiveDepts(scope) {
  const [inactive, setInactive] = useState([]);
  useEffect(() => {
    if (!scope) return;
    const load = () => api.repartiSettings(scope.tenant, scope.hotel).then((s) => setInactive(s.inactive || [])).catch(() => {});
    load();
    window.addEventListener("leutelia:reparti-settings", load);
    return () => window.removeEventListener("leutelia:reparti-settings", load);
  }, [scope?.tenant, scope?.hotel]); // eslint-disable-line react-hooks/exhaustive-deps
  return inactive;
}

function RepartiMenu({ active, onNav, scope }) {
  const inactive = useInactiveDepts(scope);
  const activeDept = active.startsWith("reparto:") ? active.split(":")[1] : null;
  const [openId, setOpenId] = useState(null);
  const shown = openId || activeDept;
  return (
    <div className="ml-6 border-l border-brand-cream/20 pl-3 space-y-0.5" data-testid="nav-reparti-list">
      {DEPTS.filter((d) => !inactive.includes(d.id)).map((d, i) => {
        const on = activeDept === d.id;
        const expanded = shown === d.id;
        return (
          <div key={d.id} data-testid={`nav-reparto-group-${d.id}`}>
            <div className="flex items-center">
              <button onClick={() => onNav(`reparto:${d.id}`)} data-testid={`nav-reparto-${d.id}`} className={`flex-1 text-left py-1.5 text-[12.5px] ${on ? "text-brand-cream font-semibold" : "text-brand-cream/55 hover:text-brand-cream"}`}><span className="font-mono text-[10px] text-brand-gold/60 mr-1.5">{String(i + 1).padStart(2, "0")}</span>{d.label}</button>
              <button onClick={() => setOpenId(expanded ? "none" : d.id)} data-testid={`nav-reparto-toggle-${d.id}`} aria-label="Apri sotto-aree" className="p-1 text-brand-cream/45 hover:text-brand-gold"><ChevronDown size={13} className={`transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} /></button>
            </div>
            {expanded && (
              <div className="ml-4 mb-1 border-l border-brand-gold/25 pl-2 space-y-0.5 leu-in" data-testid={`nav-reparto-areas-${d.id}`}>
                {d.areas.map((a) => <button key={a} onClick={() => onNav(`reparto:${d.id}:${encodeURIComponent(a)}`)} data-testid={`nav-area-${d.id}-${a.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} className={`block w-full text-left py-1 text-[11.5px] ${active === `reparto:${d.id}:${encodeURIComponent(a)}` ? "text-brand-gold" : "text-brand-cream/50 hover:text-brand-cream"}`}>{a}</button>)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export const CASCADE = [
  { id: "target", label: "Target", icon: Crosshair },
  { id: "sito", label: "Analisi del sito", icon: Search },
  { id: "seo", label: "SEO", icon: TrendingUp },
  { id: "branding", label: "Branding", icon: Tag },
  { id: "fotografia", label: "Fotografia", icon: Camera },
  { id: "marketing", label: "Marketing", icon: BarChart3 },
];
export const EXTERNAL = [
  { id: "google", label: "Google", icon: Globe },
  { id: "social", label: "Social", icon: Share2 },
  { id: "ota", label: "OTA", icon: Building2 },
  { id: "recensioni", label: "Recensioni", icon: Star },
];
const SECONDARY = [
  { id: "report", label: "Report", icon: FileText },
  { id: "pacchetti", label: "Pacchetti", icon: Layers },
  { id: "impostazioni", label: "Impostazioni", icon: Settings },
  { id: "aiuto", label: "Aiuto", icon: HelpCircle },
];

function Item({ item, active, onClick, step, locked }) {
  const Icon = item.icon;
  const on = active === item.id;
  return (
    <button
      data-testid={`nav-${item.id}`}
      onClick={() => onClick(item.id)}
      className={`group flex items-center gap-3 w-full px-4 py-2.5 rounded-lg text-[14px] transition-colors duration-150 ${
        on
          ? "bg-brand-greenSoft text-brand-cream"
          : "text-brand-cream/75 hover:text-brand-cream hover:bg-white/5"
      }`}
    >
      <Icon size={18} className={on ? "text-brand-gold" : "text-brand-gold/80 group-hover:text-brand-gold"} strokeWidth={1.75} />
      <span className={`flex-1 text-left ${on ? "font-semibold" : "font-medium"}`}>{item.label}</span>
      {locked ? <Lock size={13} className="text-brand-gold/70" data-testid={`nav-lock-${item.id}`} /> : step && <span className="text-[10px] font-mono text-brand-gold/60">{step}</span>}
    </button>
  );
}

function GroupLabel({ children }) {
  return <div className="px-4 pt-4 pb-1.5 text-[10px] font-bold tracking-[0.2em] text-brand-gold/70 uppercase">{children}</div>;
}

function Villa() {
  return (
    <svg viewBox="0 0 220 70" width="100%" height="64" fill="none" stroke="#C6902A" strokeWidth="1.1" opacity="0.85" aria-hidden>
      <path d="M6 64 h208" strokeWidth="1" />
      <path d="M20 64 V40 h20 v24 M24 44 v16 M32 44 v16" />
      <path d="M18 40 l12 -10 l12 10" />
      <path d="M60 64 V30 h26 v34 M66 36 v22 M74 36 v22 M82 36 v22" />
      <path d="M58 30 l16 -12 l16 12" />
      <path d="M104 64 V44 h16 v20 M110 48 v12" />
      <path d="M102 44 l10 -8 l10 8" />
      <path d="M140 64 V34 h30 v30 M148 40 v20 M158 40 v20 M166 40 v20" />
      <path d="M138 34 l17 -12 l17 12" />
      <path d="M188 64 V46 c0 -8 12 -8 12 0 V64" />
      <path d="M194 46 v-8" />
    </svg>
  );
}

export default function Sidebar({ active, onNav, open, onClose, isAdmin, scope, settings, showGruppo, soluzioniOn }) {
  const locked = (id) => !isAdmin && !moduleAllowed(settings, id);
  return (
    <>
    {open && <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={onClose} data-testid="sidebar-backdrop" />}
    <aside data-testid="sidebar" className={`w-[258px] shrink-0 bg-brand-green flex flex-col h-screen z-50 transition-transform duration-200 fixed top-0 left-0 lg:sticky lg:translate-x-0 ${open ? "translate-x-0" : "-translate-x-full"}`}>
      <div className="px-6 pt-6 pb-4">
        <Logo />
      </div>
      <nav className="px-3 flex-1 overflow-y-auto leutelia-scroll" data-testid="sidebar-nav">
        <Item item={{ id: "panoramica", label: "Panoramica", icon: Home }} active={active} onClick={onNav} />
        <Item item={{ id: "analisi", label: "Analisi dell'hotel", icon: BarChart3 }} active={active} onClick={onNav} />
        <GroupLabel>Intelligenza a cascata</GroupLabel>
        <div className="space-y-0.5" data-testid="nav-group-cascade">
          {CASCADE.filter((i) => !locked(i.id)).map((i, idx) => <Item key={i.id} item={i} active={active} onClick={onNav} step={`0${idx + 1}`} />)}
        </div>
        {EXTERNAL.some((i) => !locked(i.id)) && <GroupLabel>Fonti esterne</GroupLabel>}
        <div className="space-y-0.5" data-testid="nav-group-external">
          {EXTERNAL.filter((i) => !locked(i.id)).map((i) => <Item key={i.id} item={i} active={active} onClick={onNav} />)}
        </div>
        <div className="my-3 mx-4 border-t border-brand-gold/25" />
        <button onClick={() => onNav("priorita")} data-testid="nav-priorita" className={`mx-3 mb-1 w-[calc(100%-1.5rem)] text-left rounded-xl px-3 py-2.5 flex items-center gap-3 border transition-colors ${active === "priorita" ? "bg-brand-gold text-brand-greenDark border-brand-gold" : "bg-brand-gold/10 border-brand-gold/40 text-brand-cream hover:bg-brand-gold/20"}`}>
          <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${active === "priorita" ? "bg-brand-greenDark text-brand-gold" : "bg-brand-gold text-brand-greenDark"}`}><Target size={16} strokeWidth={2} /></span>
          <span className="min-w-0"><span className="block font-serif text-[16px] leading-tight">Priorità</span><span className={`block text-[10.5px] tracking-wide ${active === "priorita" ? "text-brand-greenDark/80" : "text-brand-gold"}`}>I consigli di LEUTELIA, settimana per settimana</span></span>
        </button>
        {soluzioniOn && (
          <button onClick={() => onNav("soluzioni")} data-testid="nav-soluzioni" className={`mx-3 mb-1 w-[calc(100%-1.5rem)] text-left rounded-xl px-3 py-2.5 flex items-center gap-3 border transition-colors ${String(active).startsWith("soluzioni") ? "bg-brand-gold text-brand-greenDark border-brand-gold" : "bg-brand-gold/10 border-brand-gold/40 text-brand-cream hover:bg-brand-gold/20"}`}>
            <span className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${String(active).startsWith("soluzioni") ? "bg-brand-greenDark text-brand-gold" : "bg-brand-gold text-brand-greenDark"}`}><Wrench size={16} strokeWidth={2} /></span>
            <span className="min-w-0"><span className="block font-serif text-[16px] leading-tight">Soluzioni Pratiche</span><span className={`block text-[10.5px] tracking-wide ${String(active).startsWith("soluzioni") ? "text-brand-greenDark/80" : "text-brand-gold"}`}>Problema → soluzione → quanto recuperi</span></span>
          </button>
        )}
        <div className="space-y-0.5">
          {showGruppo && <Item item={{ id: "gruppo", label: "Gruppo", icon: Building2 }} active={active} onClick={onNav} />}
          {SECONDARY.map((i) => <Item key={i.id} item={i} active={active} onClick={onNav} />)}
          {(!locked("reparti") || isAdmin) && <>
            <div className="mt-5 mb-2 px-3 text-[10px] font-bold tracking-[0.2em] text-brand-gold uppercase">Strumenti di gestione</div>
            <Item item={{ id: "reparti", label: "Direzione & Reparti", icon: Users }} active={active} onClick={onNav} />
            <RepartiMenu active={active} onNav={onNav} scope={scope} />
            <Item item={{ id: "risultati", label: "Risultati", icon: Award }} active={active} onClick={onNav} />
          </>}
          {isAdmin && <Item item={{ id: "admin", label: "Amministrazione", icon: ShieldCheck }} active={active} onClick={onNav} />}
        </div>
      </nav>
      <div className="px-6 pt-3 pb-5">
        <Villa />
        <div className="mt-3 border-t border-brand-gold/30 pt-3 text-brand-gold" style={{ letterSpacing: "0.14em", fontSize: 10.5, fontWeight: 700, lineHeight: 1.5 }}>
          DATI<br />CHE VALORIZZANO<br />L'OSPITALITÀ
        </div>
      </div>
    </aside>
    </>
  );
}
